import { z } from "zod";

import { CommonModels } from "@/data/common/common.models";
import { AppRestClient } from "@/util/rest/clients/app-rest-client";

import { BizProfileModels } from "./bizProfile.models";

export namespace BizProfileApi {
  export const requestUpload = (data: CommonModels.MediaUploadFileRequest) => {
    return AppRestClient.post(
      { resSchema: CommonModels.MediaUploadInstructionsSchema },
      `/biz/profile/request-upload`,
      data,
    );
  };

  export const bulkCreate = (data: BizProfileModels.BulkCreateBizProfileRequestDto) => {
    return AppRestClient.post(
      { resSchema: BizProfileModels.BulkCreateBizProfileResponseDtoSchema },
      `/biz/profile/bulk-create`,
      data,
    );
  };

  export const list = (
    limit: number,
    order?: string,
    page?: number,
    cursor?: string,
    filterExperienceYearsMax?: number,
    filterExperienceYearsMin?: number,
    filterRating?: BizProfileModels.BizProfileListFilterRatingParam,
    filterMsaLocation?: string,
    filterTagName?: BizProfileModels.BizProfileListFilterTagNameParam,
    filterJobTitle?: string,
    filterSearch?: string,
  ) => {
    return AppRestClient.get({ resSchema: BizProfileModels.BizProfileListResponseSchema }, `/biz/profile/list`, {
      params: {
        order,
        limit,
        page,
        cursor,
        "filter[experienceYears][max]": filterExperienceYearsMax,
        "filter[experienceYears][min]": filterExperienceYearsMin,
        "filter[rating]": filterRating,
        "filter[msaLocation]": filterMsaLocation,
        "filter[tagName]": filterTagName,
        "filter[jobTitle]": filterJobTitle,
        "filter[search]": filterSearch,
      },
    });
  };

  export const bulkDelete = (data: BizProfileModels.BulkDeleteBizProfileRequestDto) => {
    return AppRestClient.post(
      { resSchema: BizProfileModels.BulkDeleteBizProfileResponseDtoSchema },
      `/biz/profile/bulk-delete`,
      data,
    );
  };

  export const bulkUpdate = (data: BizProfileModels.BulkUpdateBizProfileRequestDto) => {
    return AppRestClient.post(
      { resSchema: BizProfileModels.BulkUpdateBizProfileResponseDtoSchema },
      `/biz/profile/bulk-update`,
      data,
    );
  };

  export const listLocations = (
    limit: number,
    order?: string,
    page?: number,
    cursor?: string,
    filterLocation?: string,
  ) => {
    return AppRestClient.get(
      { resSchema: BizProfileModels.ListLocationsResponseSchema },
      `/biz/profile/locations/list`,
      {
        params: {
          order,
          limit,
          page,
          cursor,
          "filter[location]": filterLocation,
        },
      },
    );
  };

  export const listJobTitles = (
    limit: number,
    order?: string,
    page?: number,
    cursor?: string,
    filterExperienceYears?: BizProfileModels.ExperienceYearsFilterDto,
    filterMsaLocation?: string,
    filterJobTitle?: string,
  ) => {
    return AppRestClient.get(
      { resSchema: BizProfileModels.ListJobTitlesResponseSchema },
      `/biz/profile/job-titles/list`,
      {
        params: {
          order,
          limit,
          page,
          cursor,
          "filter[experienceYears]": filterExperienceYears,
          "filter[msaLocation]": filterMsaLocation,
          "filter[jobTitle]": filterJobTitle,
        },
      },
    );
  };

  export const listJobPositions = (
    limit: number,
    order?: string,
    page?: number,
    cursor?: string,
    filterJobPosition?: string,
  ) => {
    return AppRestClient.get(
      { resSchema: BizProfileModels.ListJobPositionsResponseSchema },
      `/biz/profile/job-positions/list`,
      {
        params: {
          order,
          limit,
          page,
          cursor,
          "filter[jobPosition]": filterJobPosition,
        },
      },
    );
  };

  export const markAllAsRead = (data: BizProfileModels.BizProfileMarkAllAsReadRequestDto) => {
    return AppRestClient.post({ resSchema: z.void() }, `/biz/profile/mark-all-as-read`, data);
  };

  export const get = (id: string) => {
    return AppRestClient.get({ resSchema: BizProfileModels.ShowBizProfileDtoSchema }, `/biz/profile/${id}`);
  };

  export const update = (id: string, data: BizProfileModels.UpdateBizProfileRequestDto) => {
    return AppRestClient.post({ resSchema: BizProfileModels.ShowBizProfileDtoSchema }, `/biz/profile/${id}`, data);
  };

  export const deleteBizProfileById = (id: string) => {
    return AppRestClient.delete(
      { resSchema: BizProfileModels.DeleteBizProfileResponseDtoSchema },
      `/biz/profile/${id}`,
    );
  };

  export const favorite = (id: string) => {
    return AppRestClient.post(
      { resSchema: BizProfileModels.FavoriteBizProfileResponseDtoSchema },
      `/biz/profile/${id}/favorite`,
    );
  };

  export const unfavorite = (id: string) => {
    return AppRestClient.delete(
      { resSchema: BizProfileModels.UnfavoriteBizProfileResponseDtoSchema },
      `/biz/profile/${id}/favorite`,
    );
  };

  export const getJobSkillMatrix = (id: string) => {
    return AppRestClient.get(
      { resSchema: BizProfileModels.ProfileJobSkillMatrixResponseDtoSchema },
      `/biz/profile/${id}/job-skill-matrix`,
    );
  };

  export const retryProcessingPipeline = (id: string) => {
    return AppRestClient.post(
      { resSchema: BizProfileModels.RetryProcessingPipelineResponseDtoSchema },
      `/biz/profile/${id}/pipeline/retry`,
    );
  };

  export const downloadResume = (id: string) => {
    return AppRestClient.get(
      { resSchema: BizProfileModels.ProfileResumeDownloadResponseDtoSchema },
      `/biz/profile/${id}/download`,
    );
  };

  export const markAsRead = (id: string) => {
    return AppRestClient.post({ resSchema: z.void() }, `/biz/profile/${id}/read`);
  };
}
